import React from "react";

import _ from "lodash";

import useDragAndDropList, { DragAndDropListHook } from "commons/hooks/useDragAndDropList";

import {
    FilterLabels,
    ListCallInterface,
    ListRequest,
    ListResponse,
    ListXlsxExportCallInterface,
    SortType,
} from "commons/types/api/common";
import { OptionsType } from "commons/types/http";
import {
    Column,
    DisableOnRowClickInterface,
    EmphasizeRowInterface,
    FilterType,
    GetElementsInterface,
    LoadTableDataInterface,
    OnHeaderCellClickInterface,
    OnRowClickInterface,
    SortDirection,
} from "commons/types/table";

import HttpError from "commons/utils/errors/HttpError";
import fileDownload from "commons/utils/fileDownload";

import useControlledButton, { ControlledButtonHook } from "./useControlledButton";

export type CallEnabledFilterInterface = (filters: Array<FilterType>) => boolean;

export type ParamsType<T> = {
    apiCall: ListCallInterface<T>;
    exportApiCall?: ListXlsxExportCallInterface;
    filterLabels?: Array<FilterLabels>;
    columns: Array<Column<T>>;
    getElements: GetElementsInterface<T>;
    onRowClick?: OnRowClickInterface<T>;
    disableOnRowClick?: DisableOnRowClickInterface<T>;
    emphasizeRow?: EmphasizeRowInterface<T>;
    searchIndex?: number;
    filters?: Array<FilterType>;
    extraFilters?: Array<FilterType>;
    sortDefault?: SortType;
    callEnabledFilter?: CallEnabledFilterInterface;
    size?: number;
    initialPage?: number;
    apiCallOptions?: OptionsType;
    handleCatch?: () => void;
    hideTableHead?: boolean;
    hideTableFooter?: boolean;
    extraCallParams?: { [x: string]: any };
    withDragAndDrop?: boolean;
    handleUpdateListOrder?: (
        list: Array<T>,
        page: number,
        loadTableData: () => AbortController | undefined
    ) => void;
};

export type DataTableHook<T> = {
    onHeaderCellClick: OnHeaderCellClickInterface<T>;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    setSort: React.Dispatch<React.SetStateAction<SortType>>;
    loadTableData: LoadTableDataInterface;
    exportXlsxListButton?: ControlledButtonHook;
    loading: boolean;
    rows: Array<JSX.Element>;
    columns: Array<Column<T>>;
    sort: SortType;
    totalElements: number;
    elements: Array<T>;
    page: number;
    apiError: HttpError | undefined;
    emptyTableMessageAvailable: boolean;
    size: number;
    hideTableHead?: boolean;
    hideTableFooter?: boolean;
    dragAndDropHook?: DragAndDropListHook<HTMLTableRowElement>;
};

export function calculateFilters(filters: Array<FilterType>) {
    return JSON.stringify(filters);
}

export function calculateSort(sort: SortType) {
    return sort.col && sort.dir ? sort.col + "," + sort.dir : null;
}

function useDataTable<T>({
    apiCall,
    exportApiCall,
    filterLabels = [],
    columns,
    onRowClick,
    disableOnRowClick,
    emphasizeRow,
    getElements,
    searchIndex = 0,
    filters = undefined,
    extraFilters = undefined,
    sortDefault = {
        col: "",
        dir: SortDirection.NotOrdered,
    },
    callEnabledFilter,
    size = 15,
    initialPage,
    apiCallOptions,
    handleCatch,
    hideTableHead = false,
    hideTableFooter = false,
    extraCallParams,
    withDragAndDrop = false,
    handleUpdateListOrder,
}: ParamsType<T>): DataTableHook<T> {
    const [loading, setLoading] = React.useState<boolean>(true);
    const [rows, setRows] = React.useState<Array<JSX.Element>>([]);
    const [page, setPage] = React.useState<number>(initialPage ?? 0);
    const [sort, setSort] = React.useState<SortType>(sortDefault);

    const [apiError, setApiError] = React.useState<HttpError | undefined>(undefined);

    const [list, setList] = React.useState<ListResponse<T>>({
        content: [],
        total_elements: 0,
        total_pages: 0,
    } as ListResponse<T>);

    const [listRequest, setListRequest] = React.useState<ListRequest>({
        filter: calculateFilters([...(filters ?? []), ...(extraFilters ?? [])]),
        page: page,
        size: size,
        sort: calculateSort(sort),
        i: searchIndex,
    });

    const loadTableData: LoadTableDataInterface = React.useCallback(
        (loadParams?: { rejectFetchError?: boolean }): AbortController | undefined => {
            const canMakeCall =
                callEnabledFilter === undefined ||
                callEnabledFilter(JSON.parse(listRequest.filter));

            if (canMakeCall) {
                setLoading(true);

                setApiError(undefined);

                const param: ListRequest = {
                    ...extraCallParams,
                    filter: listRequest.filter,
                    page: listRequest.page,
                    size: listRequest.size,
                    sort: listRequest.sort,
                    i: listRequest.i,
                };

                const [apiTableDataCall, abortController] = apiCall(param, {
                    rejectHttpError: true,
                    rejectFetchError: loadParams?.rejectFetchError ? true : false,
                    manageRetry: true,
                    ...apiCallOptions,
                });

                apiTableDataCall
                    .then((response: ListResponse<T>) => {
                        setList(response);

                        setPage((prevPage) => {
                            // If page searched is greater than pages founded set table to first page
                            if (prevPage >= response.total_pages) {
                                return 0;
                            }

                            return prevPage;
                        });
                    })
                    .catch((error) => {
                        if (error instanceof HttpError) {
                            setApiError(error);

                            if (handleCatch) {
                                handleCatch();
                            }
                        }
                    })
                    .finally(() => {
                        setLoading(false);
                    });

                return abortController;
            }

            return undefined;
        },
        [
            apiCall,
            callEnabledFilter,
            handleCatch,
            listRequest.filter,
            listRequest.page,
            listRequest.size,
            listRequest.sort,
            listRequest.i,
            apiCallOptions,
            extraCallParams,
        ]
    );

    React.useEffect(() => {
        const abortController = loadTableData();

        return () => {
            if (abortController) {
                abortController.abort();
            }
        };
    }, [loadTableData]);

    React.useEffect(() => {
        setListRequest((prevListRequest: ListRequest) => {
            return {
                ...prevListRequest,
                filter: calculateFilters([...(filters ?? []), ...(extraFilters ?? [])]),
                page: initialPage ?? 0,
            };
        });

        setPage(initialPage ?? 0);
    }, [filters, extraFilters, initialPage]);

    React.useEffect(() => {
        setListRequest((prevListRequest: ListRequest) => {
            return {
                ...prevListRequest,
                i: searchIndex,
            };
        });
    }, [searchIndex]);

    React.useEffect(() => {
        setListRequest((prevListRequest: ListRequest) => {
            return {
                ...prevListRequest,
                page: page,
            };
        });
    }, [page]);

    React.useEffect(() => {
        setListRequest((prevListRequest: ListRequest) => {
            return {
                ...prevListRequest,
                sort: calculateSort(sort),
            };
        });
    }, [sort]);

    const onUpdateList = React.useCallback(
        (newList) => {
            setList((prevList) => {
                const tmpList = _.cloneDeep(prevList);

                tmpList.content = newList;

                return tmpList;
            });

            if (handleUpdateListOrder) {
                handleUpdateListOrder(newList, page, loadTableData);
            }
        },
        [handleUpdateListOrder, loadTableData, page]
    );

    const dragAndDropHook = useDragAndDropList({ list: list.content, onUpdateList });

    React.useEffect(() => {
        setRows(
            getElements({
                data: list.content,
                columns,
                onRowClick,
                disableOnRowClick,
                emphasizeRow,
                hideTableHead,
                dragAndDropHook: withDragAndDrop ? dragAndDropHook : undefined,
                loading,
            })
        );
    }, [
        getElements,
        list.content,
        columns,
        hideTableHead,
        onRowClick,
        emphasizeRow,
        disableOnRowClick,
        withDragAndDrop,
        dragAndDropHook,
        loading,
    ]);

    const onHeaderCellClick = React.useCallback((column: Column<T>) => {
        setSort((prevSort: SortType) => {
            const newSort: SortType = { col: column.id, dir: SortDirection.Asc };

            if (prevSort.col === column.id) {
                if (prevSort.dir === SortDirection.Asc) {
                    newSort.dir = SortDirection.Desc;
                } else {
                    newSort.dir = SortDirection.Asc;
                }
            }

            return newSort;
        });

        setPage(0);
    }, []);

    const exportXlsxListHandler = React.useCallback(() => {
        return new Promise((resolve) => {
            if (exportApiCall) {
                const [apiCall] = exportApiCall(
                    {
                        filter: listRequest.filter,
                        sort: listRequest.sort,
                    },
                    {
                        filter_labels: filterLabels,
                    }
                );

                apiCall
                    .then((response) =>
                        fileDownload(
                            response.file_content,
                            response.filename,
                            "application/octet-stream"
                        )
                    )
                    .finally(() => {
                        resolve(true);
                    });
            } else {
                resolve(true);
            }
        });
    }, [exportApiCall, listRequest.filter, listRequest.sort, filterLabels]);

    const exportXlsxListButton = useControlledButton({ onClickHandler: exportXlsxListHandler });

    return React.useMemo(
        () => ({
            onHeaderCellClick,
            setPage,
            setSort,
            loadTableData,
            exportXlsxListButton: exportApiCall ? exportXlsxListButton : undefined,
            loading,
            rows,
            columns,
            sort,
            totalElements: list.total_elements,
            elements: list.content,
            page,
            apiError,
            size,
            emptyTableMessageAvailable:
                list.total_elements === 0 &&
                !loading &&
                !apiError &&
                (filters === undefined || filters.length === 0),
            hideTableHead,
            hideTableFooter,
            dragAndDropHook: withDragAndDrop ? dragAndDropHook : undefined,
        }),
        [
            onHeaderCellClick,
            setPage,
            setSort,
            loadTableData,
            exportApiCall,
            exportXlsxListButton,
            loading,
            rows,
            columns,
            sort,
            list.total_elements,
            list.content,
            page,
            apiError,
            filters,
            size,
            hideTableHead,
            hideTableFooter,
            withDragAndDrop,
            dragAndDropHook,
        ]
    );
}

export default useDataTable;
